import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Add as AddIcon, Edit as EditIcon } from "@mui/icons-material";
import axios from "../../utils/axios";

const LICENSE_STATUS = ["ACTIVE", "EXPIRED", "SUSPENDED", "TRIAL"];

const statusColor = {
  ACTIVE: "success",
  TRIAL: "info",
  EXPIRED: "error",
  SUSPENDED: "warning",
};

const emptyForm = {
  licenseId: null,
  tenantId: "",
  startDate: "",
  expiryDate: "",
  licenseStatus: "ACTIVE",
  amount: "",
  notes: "",
};

function LicensePage() {
  const { t } = useTranslation();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msoTenants, setMsoTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fetchLicenses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/v1/software-license");
      setLicenses(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMsoTenants = useCallback(async () => {
    try {
      const res = await axios.post("/api/v1/tenant/auto-search", {
        filters: [
          {
            fieldName: "businessType",
            fieldValue: "MSO",
            op: "eq",
            dataType: "string",
            isJoin: false,
          },
        ],
        pagination: { pageNumber: 1, pageSize: 200 },
        sortFields: [{ field: "tenantName", sort: "ASC" }],
      });
      setMsoTenants(res.data?.data ?? []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    fetchLicenses();
    fetchMsoTenants();
  }, [fetchLicenses, fetchMsoTenants]);

  const handleOpen = (row = null) => {
    setError("");
    if (row) {
      setForm({
        licenseId: row.licenseId,
        tenantId: row.tenantId,
        startDate: row.startDate ?? "",
        expiryDate: row.expiryDate ?? "",
        licenseStatus: row.licenseStatus ?? "ACTIVE",
        amount: row.amount ?? "",
        notes: row.notes ?? "",
      });
    } else {
      setForm(emptyForm);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setError("");
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = async () => {
    setError("");
    if (!form.tenantId || !form.startDate || !form.expiryDate || !form.licenseStatus) {
      setError("Tenant, Start Date, Expiry Date and Status are required.");
      return;
    }
    setSaving(true);
    try {
      if (form.licenseId) {
        await axios.put(`/api/v1/software-license/${form.licenseId}`, form);
      } else {
        await axios.post("/api/v1/software-license", form);
      }
      handleClose();
      fetchLicenses();
    } catch (e) {
      setError(e.message || "Failed to save license.");
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { field: "licenseId", headerName: "ID", width: 70 },
    { field: "tenantName", headerName: "MSO / Tenant", flex: 1, minWidth: 180 },
    { field: "startDate", headerName: "Start Date", width: 130 },
    { field: "expiryDate", headerName: "Expiry Date", width: 130 },
    {
      field: "licenseStatus",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Chip label={params.value} color={statusColor[params.value] ?? "default"} size="small" />
      ),
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 110,
      renderCell: (params) => (params.value != null ? `₹${params.value}` : "-"),
    },
    { field: "notes", headerName: "Notes", flex: 1, minWidth: 150 },
    {
      field: "actions",
      headerName: "Actions",
      width: 90,
      sortable: false,
      renderCell: (params) => (
        <Button size="small" startIcon={<EditIcon />} onClick={() => handleOpen(params.row)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Box p={4}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={600}>
          Software License Management
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Add License
        </Button>
      </Box>

      <DataGrid
        rows={licenses}
        columns={columns}
        getRowId={(row) => row.licenseId}
        loading={loading}
        autoHeight
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        disableRowSelectionOnClick
      />

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{form.licenseId ? "Edit License" : "Add License"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} mt={0.5}>
            {error && (
              <Grid item xs={12}>
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>MSO Tenant *</InputLabel>
                <Select
                  name="tenantId"
                  value={form.tenantId}
                  label="MSO Tenant *"
                  onChange={handleChange}
                >
                  {msoTenants.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date *"
                name="startDate"
                value={form.startDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="date"
                label="Expiry Date *"
                name="expiryDate"
                value={form.expiryDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Status *</InputLabel>
                <Select
                  name="licenseStatus"
                  value={form.licenseStatus}
                  label="Status *"
                  onChange={handleChange}
                >
                  {LICENSE_STATUS.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Amount (₹)"
                name="amount"
                value={form.amount}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes"
                name="notes"
                value={form.notes}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LicensePage;
